import {
  requestBannersRefresh,
  subscribeToBannersUpdates,
} from "@braze/web-sdk";
import { brazeSetUser, setBrazeSessionReadyCallback } from "./brazeHelper";
import { clearAllCachedBannerHeights } from "./brazeBannerHeightCache";
import { initBaseNavHeightCssVars } from "./navHeightCssVars";
import { getUser, getVisitorId } from "./helperMethods";
import {
  DESKTOP_BRAZE_PLACEMENT_IDS,
  MOBILE_BRAZE_PLACEMENT_IDS,
  ALL_BRAZE_PLACEMENT_IDS,
} from "./brazePlacementIds";

export {
  ALL_BRAZE_PLACEMENT_IDS as REWARDS_PAGE_BRAZE_PLACEMENT_IDS,
  type BrazePlacementId as RewardsPageBrazePlacementId,
} from "./brazePlacementIds";

const BRAZE_ENABLED = Boolean(process.env.REACT_APP_ENABLE_BRAZE_TRACKING);
const FORCE_BANNER_REFRESH = Boolean(
  process.env.REACT_APP_BRAZE_FORCE_BANNER_REFRESH
);

/** Wait for post-session API before trusting SDK cache (rate-limit safe fallback). */
export const BANNER_FRESH_WAIT_MS = 3000;

export type BannerResolutionPhase =
  | "idle"
  | "awaiting_fresh"
  | "fresh"
  | "cache_fallback";

const listeners = new Set<() => void>();
let subscriptionId: string | null = null;
let sessionCacheCleared = false;
let brazeSessionReady = false;
let pendingBrazeUserId: string | null = null;
let lastFetchedKey: string | null = null;
let lastBrazeUserId: string | null = null;
let activeFetchKey: string | null = null;
let bannerResolution: BannerResolutionPhase = "idle";
let freshWaitTimer: ReturnType<typeof setTimeout> | null = null;

/** Resolve the Braze user id: auth context, then stored account id, then visitor id. */
export function resolveBrazeUserId(authUserId?: string | null): string {
  if (authUserId != null && authUserId !== "") {
    return String(authUserId);
  }

  const storedUser = getUser();
  if (storedUser?.id != null && storedUser.id !== "") {
    return String(storedUser.id);
  }

  const visitorId = getVisitorId();
  if (visitorId) {
    return String(visitorId);
  }

  return "";
}

export function getBannerResolutionPhase(): BannerResolutionPhase {
  return bannerResolution;
}

/** True when placements may read getBanner() and render (fresh API or cache fallback). */
export function canResolveBannersFromSdk(): boolean {
  return (
    bannerResolution === "fresh" || bannerResolution === "cache_fallback"
  );
}

function getPlacementIdsForDevice(): readonly string[] {
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  return isMobile ? MOBILE_BRAZE_PLACEMENT_IDS : DESKTOP_BRAZE_PLACEMENT_IDS;
}

function buildFetchKey(userId: string, placementIds: readonly string[]): string {
  return `${userId}:${[...placementIds].sort().join("|")}`;
}

function clearBrazeBannerSessionCacheOnce() {
  if (sessionCacheCleared) return;
  try {
    const apiKey = process.env.REACT_APP_BRAZE_API_KEY;
    localStorage.removeItem("ab.storage.banners.sessionId");
    if (apiKey) {
      localStorage.removeItem(`ab.storage.banners.sessionId.${apiKey}`);
    }
    sessionCacheCleared = true;
  } catch {
    // storage blocked
  }
}

function clearFreshWaitTimer() {
  if (freshWaitTimer != null) {
    clearTimeout(freshWaitTimer);
    freshWaitTimer = null;
  }
}

function notifyAllPlacements() {
  activeFetchKey = null;
  listeners.forEach((listener) => {
    listener();
  });
}

function onBannersUpdatedFromSdk() {
  if (bannerResolution === "idle") {
    return;
  }

  if (bannerResolution === "awaiting_fresh") {
    bannerResolution = "fresh";
    clearFreshWaitTimer();
  } else if (bannerResolution === "cache_fallback") {
    bannerResolution = "fresh";
  }
  notifyAllPlacements();
}

function startFreshWaitTimer() {
  clearFreshWaitTimer();
  freshWaitTimer = setTimeout(() => {
    freshWaitTimer = null;
    if (bannerResolution !== "awaiting_fresh") return;
    bannerResolution = "cache_fallback";
    notifyAllPlacements();
  }, BANNER_FRESH_WAIT_MS);
}

function syncBrazeUserIdentity(userId: string) {
  if (lastBrazeUserId === userId) return;
  lastBrazeUserId = userId;
  brazeSetUser(userId);
  notifyAllPlacements();
}

function ensureGlobalSubscription() {
  if (subscriptionId != null || !BRAZE_ENABLED) return;

  const subId = subscribeToBannersUpdates(() => {
    onBannersUpdatedFromSdk();
  });
  subscriptionId = subId != null ? String(subId) : null;
}

function executeBannerFetch(userId: string) {
  const placementIds = [...getPlacementIdsForDevice()];
  const fetchKey = buildFetchKey(userId, placementIds);

  if (fetchKey === lastFetchedKey || fetchKey === activeFetchKey) {
    return;
  }

  syncBrazeUserIdentity(userId);
  activeFetchKey = fetchKey;
  lastFetchedKey = fetchKey;

  if (FORCE_BANNER_REFRESH) {
    clearBrazeBannerSessionCacheOnce();
  }

  requestBannersRefresh(placementIds);
}

function flushPendingBannerFetch() {
  if (!BRAZE_ENABLED || !brazeSessionReady || pendingBrazeUserId == null) {
    return;
  }

  ensureGlobalSubscription();
  executeBannerFetch(pendingBrazeUserId);
}

/**
 * Queue exactly one batched banner fetch for the resolved Braze user.
 * Network runs once after Braze session is ready (see brazeHelper 2s init).
 */
export function scheduleBrazeBannerFetch(authUserId?: string | null) {
  if (!BRAZE_ENABLED) return;

  pendingBrazeUserId = resolveBrazeUserId(authUserId);
  if (!pendingBrazeUserId) {
    return;
  }

  if (!brazeSessionReady) {
    return;
  }

  const fetchKey = buildFetchKey(
    pendingBrazeUserId,
    getPlacementIdsForDevice()
  );
  if (fetchKey === lastFetchedKey) {
    return;
  }

  executeBannerFetch(pendingBrazeUserId);
}

/**
 * Auth identity changed (login/logout) — clear stale layout, re-fetch, notify slots.
 */
export function handleBrazeUserIdentityChange(authUserId?: string | null) {
  if (!BRAZE_ENABLED) return;

  pendingBrazeUserId = resolveBrazeUserId(authUserId);
  lastFetchedKey = null;
  clearAllCachedBannerHeights();
  initBaseNavHeightCssVars();

  if (!brazeSessionReady || !pendingBrazeUserId) {
    notifyAllPlacements();
    return;
  }

  syncBrazeUserIdentity(pendingBrazeUserId);
  executeBannerFetch(pendingBrazeUserId);
}

function onBrazeSessionReady() {
  brazeSessionReady = true;
  bannerResolution = "awaiting_fresh";
  startFreshWaitTimer();
  flushPendingBannerFetch();
}

/** @deprecated Use handleBrazeUserIdentityChange */
export function onBrazeUserIdentityChange(authUserId?: string | null) {
  handleBrazeUserIdentityChange(authUserId);
}

/**
 * Subscribe a placement slot to shared banner updates.
 * Never triggers network — only reads SDK cache and listens for refresh completion.
 */
export function registerRewardsBrazePlacementListener(
  listener: () => void
): () => void {
  listeners.add(listener);
  ensureGlobalSubscription();

  if (canResolveBannersFromSdk()) {
    listener();
  }

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Promo landing always re-fetches — session dedup can skip the placement if the
 * user visited another page first and the SDK cache was still empty.
 */
export function schedulePromoLandingBannerFetch(
  authUserId?: string | null
): void {
  if (!BRAZE_ENABLED) return;

  pendingBrazeUserId = resolveBrazeUserId(authUserId);
  if (!pendingBrazeUserId) return;

  // Always bypass session dedup so /promo gets a fresh SDK read.
  lastFetchedKey = null;

  if (!brazeSessionReady) {
    return;
  }

  // Wait for this fetch before slots read getBanner() — avoids stale cache on /promo.
  bannerResolution = "awaiting_fresh";
  startFreshWaitTimer();
  executeBannerFetch(pendingBrazeUserId);
}

/** @deprecated Use scheduleBrazeBannerFetch */
export function prefetchAllBrazePlacements(userId?: string | null) {
  scheduleBrazeBannerFetch(userId);
}

/** @deprecated Use scheduleBrazeBannerFetch */
export function ensureBrazeBannersFetched(userId?: string | null) {
  scheduleBrazeBannerFetch(userId);
}

/** @deprecated Use scheduleBrazeBannerFetch */
export function requestAllRewardsPageBannersRefresh(userId?: string | null) {
  scheduleBrazeBannerFetch(userId);
}

export function resetRewardsBrazeCoordinatorSession() {
  sessionCacheCleared = false;
  lastFetchedKey = null;
  lastBrazeUserId = null;
  activeFetchKey = null;
  bannerResolution = "idle";
  clearFreshWaitTimer();
}

setBrazeSessionReadyCallback(onBrazeSessionReady);
